import router from './router'
import { getToken, removeToken } from '@/utils/auth'
import store from './store'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'

NProgress.configure({ showSpinner: false })

const WHITE_LIST = ['/login', '/404']

// 已加载动态路由标记（页面刷新时重置）
let hasDynamicRoutes = false
// 记录已添加的动态路由名称列表，用于 logout 时清理
let addedRouteNames = []

/**
 * 清理所有动态添加的路由
 */
export function resetDynamicRoutes() {
  addedRouteNames.forEach(name => {
    try { router.removeRoute(name) } catch (e) { /* 路由可能已被移除 */ }
  })
  addedRouteNames = []
  hasDynamicRoutes = false
}

/**
 * 将后端菜单树转换为 Vue Router 路由配置
 *
 * 核心策略：所有业务页面统一挂在 '/' + Layout 容器下，每个 type=2 菜单对应一个子路由。
 * 这样路由路径 = 后端菜单 path 字段，导航和路由完全对齐。
 */
function generateRoutes(menus) {
  if (!menus || !menus.length) return []

  const children = []

  for (const menu of menus) {
    if (menu.menuType === 2 && menu.path && menu.component) {
      // ===== 顶级菜单页面：直接作为 Layout 的子路由 =====
      children.push({
        path: menu.path.replace(/^\//, ''),     // 去掉前导 /
        name: 'Menu_' + menu.id,
        component: () => import(`@/${menu.component}.vue`),
        meta: { title: menu.menuName, icon: menu.icon }
      })
      // type=2 页面也可能有子页面（如会员管理下的导入页），一并递归收集
      if (menu.children && menu.children.length) {
        collectPages(menu, children)
      }
    } else if (menu.menuType === 1) {
      // ===== 目录：递归收集所有后代中的 type=2 页面 =====
      collectPages(menu, children)
    }
  }

  if (children.length === 0) return []

  // 统一返回：一个根路由包裹 Layout + 所有业务页面
  return [{
    path: '/',
    name: '__dynamic_root__',
    component: () => import('@/layout/index.vue'),
    children,
    redirect: children[0].path  // 默认重定向到第一个菜单
  }]
}

/**
 * 递归收集目录树中所有的 type=2 叶子页面
 */
function collectPages(node, target) {
  if (!node) return
  if (node.children && node.children.length) {
    for (const child of node.children) {
      if (child.menuType === 2 && child.path && child.component) {
        target.push({
          path: child.path.replace(/^\//, ''),
          name: 'Menu_' + child.id,
          component: () => import(`@/${child.component}.vue`),
          meta: { title: child.menuName, icon: child.icon }
        })
      } else if (child.menuType === 1) {
        // 子目录继续递归
        collectPages(child, target)
      }
    }
  }
}

/**
 * 将动态路由添加到 Router 实例
 */
function addDynamicRoutes(menus) {
  const dynamicRoutes = generateRoutes(menus)

  dynamicRoutes.forEach(route => {
    router.addRoute(route)
    // 记录父路由名称
    if (route.name) addedRouteNames.push(route.name)
    // 记录所有子路由名称用于退出时清理
    if (route.children) {
      route.children.forEach(child => {
        if (child.name) addedRouteNames.push(child.name)
      })
    }
  })

  // 兜底 404 必须最后添加
  router.addRoute({ path: '*', redirect: '/404', hidden: true })
  addedRouteNames.push('__catch_all__')

  hasDynamicRoutes = true
}

// ========== 路由守卫 ==========

router.beforeEach(async (to, from, next) => {
  NProgress.start()
  const token = getToken()

  if (token) {
    // 已登录：访问登录页则跳首页
    if (to.path === '/login') {
      next({ path: '/' })
      NProgress.done()
      return
    }

    // 已登录但尚未加载动态路由
    if (!hasDynamicRoutes) {
      try {
        await store.dispatch('user/getInfo')
        const menus = store.state.user.menuList
        addDynamicRoutes(menus)
        // 动态路由添加完成，重新导航到目标页面
        next({ ...to, replace: true })
      } catch (error) {
        console.error('获取用户信息失败:', error)
        removeToken()
        next(`/login?redirect=${to.path}`)
        NProgress.done()
      }
    } else {
      next()
    }
  } else {
    // 未登录：白名单放行，其余跳登录页
    if (WHITE_LIST.includes(to.path)) {
      next()
    } else {
      next(`/login?redirect=${to.path}`)
      NProgress.done()
    }
  }
})

router.afterEach(() => {
  NProgress.done()
})
